import { Box, Image, Stack, Text } from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { BoxProps } from '@chakra-ui/react';

import { LanguageSelection } from '$src/components/LanguageSelection';
import { ExitIntentContext } from '$src/context/ExitIntentContext';
import { useIsDesktop } from '$src/hooks/client/useIsDesktop';
import { useShop } from '$src/hooks/queries';
import { useROFormMessageAtom } from '$src/hooks/state';
import { useROBrandingPreviewAtom } from '$src/hooks/state/useBrandingPreviewAtom';
import { useCheckoutFeaturesAtom } from '$src/hooks/state/useCheckoutFeaturesAtom';

export const Header = (props: BoxProps) => {
  const { data: shop } = useShop();
  const { canUseCheckoutFeature } = useCheckoutFeaturesAtom();
  const brandingPreview = useROBrandingPreviewAtom();
  const exitIntentContext = useContext(ExitIntentContext);

  // REMOVE CONDITION AFTER MIGRATION -----------------------------------
  const urlLogoShop = useMemo(
    () =>
      shop?.logo?.includes('https://')
        ? shop?.logo
        : `https://${process.env.NEXT_PUBLIC_LOGOS_BUCKET}.s3.eu-west-3.amazonaws.com/${shop?.logo}`,
    [shop],
  );

  // --------------------------------------------------------------------
  const previewLogoUrl = brandingPreview?.logoUrl;
  const effectiveLogoUrl = previewLogoUrl ?? urlLogoShop;
  const hasLogo = previewLogoUrl ? true : !!shop?.logo;
  const [logoLoadFailed, setLogoLoadedFailed] = useState(false);
  const [logoSrcSet, setLogoSrcSet] = useState('');
  const { isMobile } = useIsDesktop();
  const message = useROFormMessageAtom();

  useEffect(() => {
    if (!shop) return;
    if (shop.logoVariants?.length) {
      setLogoSrcSet(
        shop.logoVariants
          ?.map((variant) => `${shop.baseMediaUrl}${variant.fileKey} ${variant.name}`)
          .join(', ') ?? '',
      );
    }
  }, [shop]);

  // Background injection is now handled by ServerBackgroundImage component for better performance

  const executeExit = useCallback(() => {
    const target = message?.origin;
    const isSafeUrl = target && /^https?:\/\//i.test(target);
    window.location.replace(isSafeUrl ? target : '/');
  }, [message?.origin]);

  const onBack = useCallback(() => {
    // Try to show exit intent popup first
    if (exitIntentContext) {
      const wasShown = exitIntentContext.triggerExitIntent();
      if (wasShown) {
        // Store the exit action to execute after user selects a reason
        // Double arrow function: outer one for React setState (functional update pattern),
        // inner one is the actual callback to store
        exitIntentContext.setPendingExitCallback(() => () => executeExit());
        return;
      }
    }
    // If popup wasn't shown (already shown or order processing), proceed with exit
    executeExit();
  }, [exitIntentContext, executeExit]);

  const showMobileBackground = canUseCheckoutFeature('show_mobile_background_image') && isMobile;

  return (
    <Box
      alignItems="start"
      justifyContent="space-between"
      {...props}
      mb="2px"
      px={4}
      py={3}
      w="100%"
      display="flex"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        w="full"
        h={!isMobile ? 'full' : undefined}
      >
        <Box
          width="33%"
          display="flex"
          justifyContent="start"
          ml={isMobile ? '0' : '6'}
          pt={isMobile ? '0' : '5'}
        >
          <ArrowBackIcon onBack={onBack} />
        </Box>

        <Box flexGrow={1} height="100%" display="flex" justifyContent="center">
          {!logoLoadFailed && hasLogo ? (
            <Image
              maxH="100%"
              h={isMobile ? '2rem' : '2.5rem'}
              alt="logo shop"
              textAlign="center"
              src={effectiveLogoUrl}
              srcSet={previewLogoUrl ? undefined : logoSrcSet}
              onError={() => setLogoLoadedFailed(true)}
            />
          ) : (
            <Text textAlign="center" py="4" color="var(--text, #1C1C1C)">
              {shop?.name ?? ''}
            </Text>
          )}
        </Box>

        <Box width="33%" display="flex" justifyContent="end" alignItems="center">
          {showMobileBackground ? (
            <LanguageSelection theme="light" />
          ) : (
            <Box mr={isMobile ? '1' : '3'} pt={isMobile ? '1' : '3'}>
              <LanguageSelection theme="dark" />
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

function ArrowBackIcon({ onBack }: { onBack: () => void }) {
  return (
    <Box
      w="24px"
      h="24px"
      onClick={onBack}
      cursor="pointer"
      _hover={{ bg: 'none' }}
      data-ph-capture-attribute-tracking-id="checkout-form-back-button"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={24}
        height={24}
        fill="none"
        role="img"
        aria-label="Close"
        style={{ color: 'var(--text, #000)' }}
      >
        <path
          fill="currentColor"
          fillOpacity={0.85}
          d="M3 12.402c0 .245.107.47.303.655l6.484 6.474c.195.186.4.274.635.274.478 0 .86-.352.86-.84a.855.855 0 0 0-.245-.615L8.85 16.123l-3.868-3.525-.205.478 3.145.195h12.353c.508 0 .86-.36.86-.869 0-.507-.352-.869-.86-.869H7.922l-3.145.196.205.488L8.85 8.682l2.187-2.227a.871.871 0 0 0 .244-.615c0-.488-.38-.84-.86-.84-.233 0-.439.078-.653.293l-6.465 6.455a.895.895 0 0 0-.303.654Z"
        />
      </svg>
    </Box>
  );
}
